import { Box, Text } from "@mantine/core"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type MouseEvent, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { MarkdownEditor } from "@/components/controls"
import type { TUser } from "@/types"
import { api, userAPI } from "@/utils"
import { Panel } from "./utils"

export function ProfileNote({ user }: { user: TUser }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [value, setValue] = useState("")

  const { data: { note } = {} } = useQuery({
    queryKey: ["details", user.id],
    queryFn: () => userAPI.details(user.id),
  })

  useEffect(() => {
    setValue(note || "")
  }, [note])

  const { mutate } = useMutation({
    mutationFn: async (newNote: string) => {
      if (newNote == note) return
      return await api.put(`/people/user/${user.id}/details`, {
        note: newNote,
      })
    },
    onMutate: async (newNote: string) => {
      if (newNote == note) return
      const previousDetails = queryClient.getQueryData(["details", user.id])
      queryClient.setQueryData(["details", user.id], (old: any) => ({
        ...old,
        note: newNote,
      }))
      return { previousDetails }
    },
  })

  const focusEditor = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const editor = event.currentTarget.querySelector<HTMLElement>('[contenteditable="true"]')
    if (!editor) return
    editor.focus()
  }, [])

  return (
    <Panel
      block
      bg="yellow.0"
      mih={300}
      xl={{
        gridRowStart: 3,
        gridRowEnd: "span 2",
        gridColumnStart: -3,
      }}
    >
      <Box h="100%" onClick={focusEditor}>
        <Text fw="bold" ta="center" c="dimmed">
          {t("Your private notes")}
        </Text>
        <MarkdownEditor
          onBlur={() => mutate(value)}
          styles={{
            root: {
              border: "none",
            },
            content: {
              fontSize: "1.1rem",
              fontStyle: "italic",
              backgroundColor: "transparent",
            },
          }}
          toolbar={false}
          value={value}
          onChange={setValue as any}
          placeholder={t("Jot down anything that you want to keep in mind. Only you can see these notes.")}
        />
      </Box>
    </Panel>
  )
}
